export interface ItemsFilterSearch {
  org?: string[];
  brand?: string[];
  category?: string[];
  location?: string[];
  incomplete?: '1';
  missingAttribute?: string;
  sort?: string;
  page?: number;
}

const ARRAY_KEYS = ['org', 'brand', 'category', 'location'] as const;

/**
 * Validate-and-normalize filter URL keys for the items list.
 *
 * The same URL is read by two systems with different parsing rules:
 *   - nuqs (in `useAssetFilters`) reads via URLSearchParams — values come back
 *     as plain strings, arrays as comma-separated strings.
 *   - TanStack Router's default `parseSearch` JSON-decodes scalars — `?page=3`
 *     becomes the number `3`, `?incomplete=1` becomes `1`.
 *
 * This helper accepts either shape and returns the canonical form (`incomplete:
 * '1'`, `org: string[]`, `page: number`). Used by route `validateSearch` schemas
 * and by `useGoToItems` to keep filter state stable across edit/create/merge
 * round-trips. See spec 2026-05-08-ahr-522.
 */
export function pickFilterSearch(search: Record<string, unknown>): ItemsFilterSearch {
  const out: ItemsFilterSearch = {};

  for (const key of ARRAY_KEYS) {
    const raw = search[key];
    if (Array.isArray(raw)) {
      const strings = raw.filter((v): v is string => typeof v === 'string');
      if (strings.length > 0) out[key] = strings;
    } else if (typeof raw === 'string' && raw.length > 0) {
      // nuqs comma-separated form: `?org=a,b` → ['a', 'b'].
      const parts = raw.split(',').filter((v) => v.length > 0);
      if (parts.length > 0) out[key] = parts;
    }
  }

  // `=== 1` is unreachable under the current URLSearchParams-based router serializer
  // (every scalar arrives as a string), kept defensively in case the serializer is
  // ever swapped back to TanStack's default JSON-decoding `parseSearch`.
  if (search.incomplete === '1' || search.incomplete === 1) out.incomplete = '1';
  if (typeof search.missingAttribute === 'string') out.missingAttribute = search.missingAttribute;
  if (typeof search.sort === 'string') out.sort = search.sort;

  if (typeof search.page === 'number' && Number.isInteger(search.page)) {
    out.page = search.page;
  } else if (typeof search.page === 'string' && /^\d+$/.test(search.page)) {
    out.page = Number.parseInt(search.page, 10);
  }

  return out;
}
